import { getLogger } from '@williamhill/rosi-ui-shared/src/lib/logger-plus';
import { partition } from 'ramda';
import { Ticket, TICKET_TYPE_VERSION, TicketView, transformLegacyData, updateTicket } from '../model';

const logger = getLogger('bets/ticket-store');

export interface TicketStore {
  getAll(): TicketView[];
  get(retailTicketId: string): TicketView | undefined;
  add(ticket: TicketView): void;
  addAll(tickets: TicketView[]): void;
  update(tickets: Ticket[]): TicketView[];
  remove(retailTicketId: string): void;
  removeAll(): void;
}
const ticketStoreKey = 'TICKET_STORE';

export class LocalTicketStore implements TicketStore {
  private tickets: TicketView[] = this.getTickets();

  getAll(): TicketView[] {
    return this.tickets;
  }
  get(retailTicketId: string): TicketView | undefined {
    return this.tickets.find(t => t.retailTicketId === retailTicketId);
  }
  add(ticket: TicketView): void {
    this.addTickets([ticket]);
  }
  addAll(tickets: TicketView[]): void {
    this.addTickets(tickets);
  }
  update(tickets: Ticket[]): TicketView[] {
    const mappedTickets = new Map<string, Ticket>(tickets.map(t => [t.retailTicketId, t]));
    const updatedTickets = this.tickets.map(t =>
      mappedTickets.has(t.retailTicketId) ? updateTicket(mappedTickets.get(t.retailTicketId) as Ticket, t) : t
    );
    this.setTickets(updatedTickets);
    return this.tickets;
  }
  remove(retailTicketId: string) {
    this.setTickets(this.tickets.filter(t => t.retailTicketId !== retailTicketId));
  }
  removeAll() {
    this.tickets = [];
    localStorage.removeItem(ticketStoreKey);
  }

  private getTickets(): TicketView[] {
    const data = localStorage.getItem(ticketStoreKey);
    return data !== null ? this.parseData(data) : [];
  }

  private setTickets(tickets: TicketView[]) {
    this.tickets = tickets;
    localStorage.setItem(ticketStoreKey, JSON.stringify(this.tickets));
  }

  private addTickets(tickets: TicketView[]) {
    const newTicketIds = new Set(tickets.map(t => t.retailTicketId));
    const filteredTickets = this.tickets.filter(t => !newTicketIds.has(t.retailTicketId));
    this.setTickets([...filteredTickets, ...tickets]);
  }

  private parseData(data: string): TicketView[] {
    const parsedData = JSON.parse(data) as TicketView[];
    if (!Array.isArray(parsedData)) {
      logger.error(parsedData, 'Invalid data for TICKET_STORE');
      return [];
    }

    const [result, invalidData] = partition(rd => rd['TICKET_TYPE_VERSION'] === TICKET_TYPE_VERSION, parsedData);
    invalidData.forEach(d => {
      /*
        TODO: change after releasing ROSI-16039, as we supposed to base transformation on ticket type version
      */
      logger.error(d, `Invalid item for Ticket View, Ticket Type Version is not ${TICKET_TYPE_VERSION}`);
      result.push(transformLegacyData(d));
    });

    if (invalidData.length > 0) {
      localStorage.setItem(ticketStoreKey, JSON.stringify(result));
    }

    return result;
  }
}

export default new LocalTicketStore();
